import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "@/components/Reveal";
import BlueprintOverlay from "@/components/BlueprintOverlay";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useStore } from "@/context/StoreContext";
import ContactDetails from "@/components/ContactDetails";
import Seo from "@/components/Seo";

const inputCls =
  "w-full border border-bone/15 bg-ink px-4 py-3.5 font-mono text-sm text-bone placeholder:text-bone-muted/60 focus:border-brass/60 focus:outline-none transition-colors";

const TYPES = ["commission", "question", "press"];

const EMPTY_FORM = {
  name: "",
  email: "",
  inquiry_type: "commission",
  archetype: "",
  canvas_scale: "40.6 × 50.8 cm / 16 × 20 in",
  message: "",
};

export default function Contact() {
  const { t } = useLang();
  const { settings } = useStore();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries", form);
      toast.success(t("contact.toastOk"));
      setForm({ ...EMPTY_FORM });
    } catch (err) {
      toast.error(t("contact.toastErr"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page" className="relative overflow-hidden">
      <Seo title={t("seo.contactTitle")} description={t("seo.contact")} />
      <BlueprintOverlay dimLabel="DEMANDE — COMMISSION" stamp="DWG-REQ-FR-000" />
      <div className="relative mx-auto grid max-w-7xl gap-16 px-6 pb-28 pt-32 sm:px-10 lg:grid-cols-5 lg:px-16">
        <Reveal className="lg:col-span-2">
          <p className="eyebrow">{t("contact.eyebrow")}</p>
          <h1 className="mt-4 font-serif text-4xl font-light leading-tight text-bone sm:text-5xl">
            {t("contact.titleA")}<br />{t("contact.titleB")}
          </h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-bone-dim/80 sm:text-base">
            {t("contact.intro")}
          </p>
          <div className="mt-10 space-y-4 border-l-2 border-brass/40 pl-6 font-mono text-[10px] leading-relaxed tracking-[0.2em] text-bone-dim">
            {t("contact.steps").map((s, i) => (
              <p key={i}>{s}</p>
            ))}
          </div>
          <p className="mt-10 max-w-md font-serif text-lg italic text-bone-dim/80">
            {t("contact.replyNote")}
          </p>
          <ContactDetails settings={settings} />
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-3">
          <form
            data-testid="contact-commission-form"
            onSubmit={submit}
            className="grid grid-cols-1 gap-4 border border-bone/10 bg-ink-2/80 p-8 backdrop-blur-sm sm:grid-cols-2 sm:p-10"
          >
            <input data-testid="contact-name-input" required placeholder={t("contact.namePh")} value={form.name} onChange={set("name")} className={inputCls} />
            <input data-testid="contact-email-input" required type="email" placeholder={t("contact.emailPh")} value={form.email} onChange={set("email")} className={inputCls} />
            <select
              data-testid="contact-type-select"
              value={form.inquiry_type}
              onChange={set("inquiry_type")}
              className={`${inputCls} sm:col-span-2`}
            >
              {TYPES.map((k) => (
                <option key={k} value={k}>{t(`contact.type.${k}`)}</option>
              ))}
            </select>
            <input
              data-testid="contact-archetype-input"
              placeholder={t("contact.archetypePh")}
              value={form.archetype}
              onChange={set("archetype")}
              className={inputCls}
            />
            <select
              data-testid="contact-scale-select"
              value={form.canvas_scale}
              onChange={set("canvas_scale")}
              className={inputCls}
            >
              <option>40.6 × 50.8 cm / 16 × 20 in</option>
              <option>Format sur mesure / Custom format</option>
            </select>
            <textarea
              data-testid="contact-message-input"
              required
              placeholder={t("contact.messagePh")}
              value={form.message}
              onChange={set("message")}
              rows={6}
              className={`${inputCls} resize-none sm:col-span-2`}
            />
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={busy}
              className="border border-brass/60 bg-brass/10 py-4 font-mono text-[11px] tracking-[0.35em] text-brass transition-all duration-300 hover:bg-brass hover:text-ink disabled:opacity-50 sm:col-span-2"
            >
              {busy ? t("contact.sending") : t("contact.submit")}
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
